import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { n11Api } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

interface N11LogEntry {
  id: string;
  timestamp: string;
  type: 'price-stock-update' | 'product-create' | 'product-save';
  productName: string;
  stockCode: string;
  taskId: number;
  status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT';
  operation: string;
  details: any;
  taskDetails?: any;
  lastChecked?: string;
}

interface N11LogModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
}

const N11LogModal: React.FC<N11LogModalProps> = ({ open, onClose, companyId }) => {
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const [logs, setLogs] = useState<N11LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<N11LogEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkingTaskDetails, setCheckingTaskDetails] = useState<string | null>(null);
  const [clearingLogs, setClearingLogs] = useState(false);

  // LocalStorage'dan logları yükle
  useEffect(() => {
    if (open && companyId) {
      loadLogsFromStorage();
    }
  }, [open, companyId]);

  // Arama ve filtreleme
  useEffect(() => {
    let filtered = [...logs];

    // Arama terimine göre filtrele
    if (searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.productName.toLowerCase().includes(lowerSearchTerm) ||
        log.stockCode.toLowerCase().includes(lowerSearchTerm) ||
        log.taskId.toString().includes(lowerSearchTerm) ||
        log.operation.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Tab'a göre filtrele
    switch (selectedTab) {
      case 1: // Başarılı
        filtered = filtered.filter(log => log.status === 'PROCESSED');
        break;
      case 2: // Beklemede
        filtered = filtered.filter(log => log.status === 'IN_QUEUE');
        break;
      case 3: // Hatalı
        filtered = filtered.filter(log => log.status === 'REJECT');
        break;
      default: // Tümü
        break;
    }

    // Tarihe göre sırala (en yeni en üstte)
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    setFilteredLogs(filtered);
  }, [logs, searchTerm, selectedTab]);

  // LocalStorage'dan logları yükle
  const loadLogsFromStorage = () => {
    try {
      const storageKey = `n11_logs_${companyId}`;
      const savedLogs = localStorage.getItem(storageKey);
      if (savedLogs) {
        const parsedLogs = JSON.parse(savedLogs);
        setLogs(parsedLogs);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error('N11 logları yüklenirken hata:', error);
      setLogs([]);
    }
  };

  // Task detaylarını kontrol et
  const checkTaskDetails = async (log: N11LogEntry) => {
    if (!companyId || !log.taskId) return;

    setCheckingTaskDetails(log.id);
    try {
      console.log(`🔍 [N11 LOG] Task detayları kontrol ediliyor - Task ID: ${log.taskId}`);

      const response = await n11Api.post(`/api/n11/products/task-details/${log.taskId}`);

      const taskDetails = response.data;
      console.log('✅ [N11 LOG] Task detayları alındı:', taskDetails);

      // Log'u güncelle
      const updatedLogs = logs.map(l => {
        if (l.id === log.id) {
          return {
            ...l,
            status: taskDetails.status || l.status,
            taskDetails: taskDetails,
            lastChecked: new Date().toISOString()
          };
        }
        return l;
      });

      setLogs(updatedLogs);

      // LocalStorage'a kaydet
      const storageKey = `n11_logs_${companyId}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedLogs));

    } catch (error) {
      console.error('❌ [N11 LOG] Task detay kontrolü hatası:', error);
    } finally {
      setCheckingTaskDetails(null);
    }
  };

  // Tamamlanan logları temizle
  const clearCompletedLogs = async () => {
    if (!companyId) return;

    const completedLogs = logs.filter(log => log.status === 'PROCESSED');
    
    if (completedLogs.length === 0) {
      return;
    }

    setClearingLogs(true);
    try {
      console.log(`🧹 [N11 LOG] ${completedLogs.length} tamamlanan log temizleniyor...`);
      
      // Sadece tamamlanmamış logları tut
      const remainingLogs = logs.filter(log => log.status !== 'PROCESSED');
      
      // State'i güncelle
      setLogs(remainingLogs);
      
      // LocalStorage'a kaydet
      const storageKey = `n11_logs_${companyId}`;
      localStorage.setItem(storageKey, JSON.stringify(remainingLogs));
      
      console.log(`✅ [N11 LOG] ${completedLogs.length} tamamlanan log temizlendi. Kalan log sayısı: ${remainingLogs.length}`);
    } catch (error) {
      console.error('❌ [N11 LOG] Log temizleme hatası:', error);
    } finally {
      setClearingLogs(false);
    }
  };

  // Tüm bekleyen işlemleri kontrol et (en eskiden en yeniye doğru)
  const checkAllPendingTasks = async () => {
    const pendingLogs = logs.filter(log => log.status === 'IN_QUEUE');
    
    if (pendingLogs.length === 0) {
      return;
    }

    // En eskiden en yeniye doğru sırala (timestamp'e göre)
    const sortedPendingLogs = pendingLogs.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    setLoading(true);
    try {
      console.log(`🔄 [N11 LOG] ${sortedPendingLogs.length} bekleyen işlem kontrol ediliyor (en eskiden en yeniye)...`);
      
      for (let i = 0; i < sortedPendingLogs.length; i++) {
        const log = sortedPendingLogs[i];
        console.log(`🔍 [N11 LOG] ${i + 1}/${sortedPendingLogs.length} - ${log.productName} (${log.taskId}) kontrol ediliyor...`);
        
        await checkTaskDetails(log);
        
        // API'ye çok hızlı istek göndermemek için kısa bir bekleme (son işlem hariç)
        if (i < sortedPendingLogs.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      console.log(`✅ [N11 LOG] Tüm bekleyen işlemler kontrol edildi.`);
    } finally {
      setLoading(false);
    }
  };

  // Durum rengini al
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSED':
        return 'success';
      case 'IN_QUEUE':
        return 'warning';
      case 'REJECT':
        return 'error';
      default:
        return 'default';
    }
  };

  // Durum ikonunu al
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PROCESSED':
        return <CheckCircleIcon fontSize="small" />;
      case 'IN_QUEUE':
        return <ScheduleIcon fontSize="small" />;
      case 'REJECT':
        return <ErrorIcon fontSize="small" />;
      default:
        return <CancelIcon fontSize="small" />;
    }
  };

  // Durum metnini al
  const getStatusText = (status: string) => {
    switch (status) {
      case 'PROCESSED':
        return t('stock.marketplaceLogs.status.processed');
      case 'IN_QUEUE':
        return t('stock.marketplaceLogs.status.inQueue');
      case 'REJECT':
        return t('stock.marketplaceLogs.status.rejected');
      default:
        return t('stock.marketplaceLogs.status.unknown');
    }
  };

  // İşlem tipini al
  const getOperationTypeText = (type: string) => {
    switch (type) {
      case 'price-stock-update':
        return t('stock.marketplaceLogs.operationTypes.priceStockUpdate');
      case 'product-create':
        return t('stock.marketplaceLogs.operationTypes.productCreate');
      case 'product-save':
        return t('stock.marketplaceLogs.operationTypes.productSave');
      default:
        return type;
    }
  };

  // Tab sayılarını hesapla
  const getTabCounts = () => {
    return {
      total: logs.length,
      processed: logs.filter(log => log.status === 'PROCESSED').length,
      inQueue: logs.filter(log => log.status === 'IN_QUEUE').length,
      rejected: logs.filter(log => log.status === 'REJECT').length
    };
  };

  const tabCounts = getTabCounts();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6" component="span">
            {t('stock.marketplaceLogs.title')}
          </Typography>
          <GuideButton guideKey="inventory.marketplaceLogs" size="small" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={clearingLogs ? <CircularProgress size={16} /> : <DeleteSweepIcon />}
            onClick={clearCompletedLogs}
            disabled={clearingLogs || loading || tabCounts.processed === 0}
            color="secondary"
          >
            {clearingLogs 
              ? t('stock.marketplaceLogs.actions.clearing')
              : tabCounts.processed === 0 
                ? t('stock.marketplaceLogs.actions.noCompletedLogs')
                : t('stock.marketplaceLogs.actions.clearCompleted', { count: tabCounts.processed })
            }
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={checkAllPendingTasks}
            disabled={loading || clearingLogs || tabCounts.inQueue === 0}
            color="primary"
          >
            {loading 
              ? t('stock.marketplaceLogs.actions.checking')
              : tabCounts.inQueue === 0 
                ? t('stock.marketplaceLogs.actions.noPending')
                : t('stock.marketplaceLogs.actions.checkPending', { count: tabCounts.inQueue })
            }
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Arama ve Tab'lar */}
        <Box sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
          <TextField
            size="small"
            placeholder={t('stock.marketplaceLogs.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, mb: 2 }}
          />

          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t('stock.marketplaceLogs.tabs.all', { count: tabCounts.total })} />
            <Tab label={t('stock.marketplaceLogs.tabs.success', { count: tabCounts.processed })} />
            <Tab label={t('stock.marketplaceLogs.tabs.pending', { count: tabCounts.inQueue })} />
            <Tab label={t('stock.marketplaceLogs.tabs.failed', { count: tabCounts.rejected })} />
          </Tabs>
        </Box>

        {/* Log Listesi */}
        <Box sx={{ height: 'calc(90vh - 200px)', overflow: 'auto', p: 2 }}>
          {filteredLogs.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              color: 'text.secondary'
            }}>
              <InfoIcon sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {searchTerm ? t('stock.marketplaceLogs.empty.search') : t('stock.marketplaceLogs.empty.noLogs')}
              </Typography>
              <Typography variant="body2">
                {searchTerm ? t('stock.marketplaceLogs.empty.searchHint') : t('stock.marketplaceLogs.empty.noLogsHint')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredLogs.map((log) => (
                <Accordion key={log.id} sx={{ border: '1px solid #ddd' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Chip
                        icon={getStatusIcon(log.status)}
                        label={getStatusText(log.status)}
                        color={getStatusColor(log.status) as any}
                        size="small"
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 150 }}>
                        {log.productName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                        {log.stockCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                        {getOperationTypeText(log.type)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                        Task: {log.taskId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        {new Date(log.timestamp).toLocaleString(dateLocale)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('stock.marketplaceLogs.actions.checkTaskDetails')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              checkTaskDetails(log);
                            }}
                            disabled={checkingTaskDetails === log.id}
                          >
                            {checkingTaskDetails === log.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* İşlem Detayları */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('stock.marketplaceLogs.details.operationDetails')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {log.operation}
                        </Typography>
                      </Box>

                      {/* Task Detayları */}
                      {log.taskDetails && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            {t('stock.marketplaceLogs.details.n11TaskResult')}
                          </Typography>
                          <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2">
                              <strong>{t('stock.marketplaceLogs.details.status')}:</strong> {getStatusText(log.taskDetails.status)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('stock.marketplaceLogs.details.created')}:</strong> {log.taskDetails.createdDate}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('stock.marketplaceLogs.details.updated')}:</strong> {log.taskDetails.modifiedDate}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{t('stock.marketplaceLogs.details.totalSku')}:</strong> {log.taskDetails.totalElements}
                            </Typography>
                            
                            {log.taskDetails.skus && log.taskDetails.skus.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {t('stock.marketplaceLogs.details.skuDetails')}
                                </Typography>
                                {log.taskDetails.skus.map((sku: any, index: number) => (
                                  <Box key={index} sx={{ ml: 2, mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                    <Typography variant="caption">
                                      <strong>{t('stock.marketplaceLogs.details.stockCode')}:</strong> {sku.itemCode}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption">
                                      <strong>{t('stock.marketplaceLogs.details.status')}:</strong> {sku.status}
                                    </Typography>
                                    {sku.sku && (
                                      <>
                                        <br />
                                        <Typography variant="caption">
                                          <strong>{t('stock.marketplaceLogs.details.salePrice')}:</strong> {sku.sku.salePrice} {sku.sku.currencyType}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption">
                                          <strong>{t('stock.marketplaceLogs.details.listPrice')}:</strong> {sku.sku.listPrice} {sku.sku.currencyType}
                                        </Typography>
                                        {sku.sku.stock !== undefined && (
                                          <>
                                            <br />
                                            <Typography variant="caption">
                                              <strong>{t('stock.marketplaceLogs.details.stock')}:</strong> {sku.sku.stock}
                                            </Typography>
                                          </>
                                        )}
                                      </>
                                    )}
                                    {sku.reasons && sku.reasons.length > 0 && (
                                      <>
                                        <br />
                                        <Typography variant="caption">
                                          <strong>{t('stock.marketplaceLogs.details.description')}:</strong> {sku.reasons.join(', ')}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                          {log.lastChecked && (
                            <Typography variant="caption" color="text.secondary">
                              {t('stock.marketplaceLogs.details.lastChecked')}: {new Date(log.lastChecked).toLocaleString(dateLocale)}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('stock.common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default N11LogModal;
